import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Filter } from "constants/filters";
import { ReportType } from "constants/reports/report-type";
import { ActionType, Target } from "interfaces/entities/audit-log";
import { IStoreState } from "interfaces/storeState";
import {
  IWithReportsViewState,
  ReportsViewActions,
  getFilters,
  getIsReportFetching,
  getReportData,
} from "store/views/reports";

export type IAuditLogItem = {
  ID: string;
  createdAt: string;
  action: ActionType;
  target: Target;
  author: IAuthor;
  entityName: string;
};

type IAuthorType =
  | "administrator"
  | "agent"
  | "api"
  | "scheduledTask"
  | "emailMessage"
  | "paymentProvider"
  | "helpdeskSystem";

export type IAuthor = {
  ID: string;
  type: IAuthorType;
  ip: string;
  userAgent: string;
  accountID?: string;
};

export type IAuditLogPayload = Record<string, string | number>;

interface IUseAuditLog {
  data: IAuditLogItem[];
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  pageSize: number;
  isFetching: boolean;
}

export const useAuditLog = (): IUseAuditLog => {
  const [pageSize] = useState(useSelector(getFilters).pageSize ?? 41);
  // @ts-expect-error TS(2339): Property 'author' does not exist on type 'unknown'... Remove this comment to see the full error message
  const { author, target, action, date } = useSelector(getFilters);
  const [direction, setDirection] = useState("");
  const data = useSelector((state: IWithReportsViewState) => getReportData(state, ReportType.AuditLog));
  const isFetching = useSelector((state: IStoreState) => getIsReportFetching(state, ReportType.AuditLog));
  const dispatch = useDispatch();

  let lastID = "";
  let firstID = "";
  let lastCreatedAt = "";
  let firstCreatedAt = "";
  let hasMoreNext = false;
  let hasMorePrev = false;

  let dataToDisplay: IAuditLogItem[] = [];

  if (Array.isArray(data) && data.length > 0) {
    if (!direction) {
      if (pageSize === data.length) {
        hasMoreNext = true;
        hasMorePrev = false;
        lastID = data[data.length - 2]?.ID;
        lastCreatedAt = data[data.length - 2]?.createdAt;
        dataToDisplay = data.slice(0, pageSize - 1);
      } else {
        hasMoreNext = false;
        hasMorePrev = false;
        dataToDisplay = [...data];
      }
    }

    if (direction === "next") {
      if (pageSize + 1 === data.length) {
        hasMoreNext = true;
        hasMorePrev = true;
        lastID = data[data.length - 2]?.ID;
        lastCreatedAt = data[data.length - 2]?.createdAt;
        firstID = data[1]?.ID;
        firstCreatedAt = data[1]?.createdAt;
        dataToDisplay = data.slice(1, data.length - 1);
      } else {
        hasMoreNext = false;
        hasMorePrev = true;
        firstID = data[1]?.ID;
        firstCreatedAt = data[1]?.createdAt;
        dataToDisplay = [...data];
      }
    }

    if (direction === "prev") {
      if (pageSize + 1 === data.length) {
        hasMoreNext = true;
        hasMorePrev = true;
        lastID = data[data.length - 2]?.ID;
        lastCreatedAt = data[data.length - 2]?.createdAt;
        firstID = data[1]?.ID;
        firstCreatedAt = data[1]?.createdAt;
        dataToDisplay = data.slice(1, data.length - 1);
      } else {
        hasMoreNext = true;
        hasMorePrev = false;
        lastID = data[data.length - 2]?.ID;
        lastCreatedAt = data[data.length - 2]?.createdAt;
        dataToDisplay = data.slice(0, data.length - 1);
      }
    }
  }

  useEffect(() => {
    dispatch(
      ReportsViewActions.updateFilter({
        [Filter.NextId]: undefined,
        [Filter.NextValue]: undefined,
        [Filter.PrevId]: undefined,
        [Filter.PrevValue]: undefined,
      })
    );
  }, [author, target, action, date, dispatch]);

  const fetchNextPage = (): void => {
    setDirection("next");
    dispatch(
      ReportsViewActions.updateFilter({
        [Filter.PageSize]: pageSize + 1,
        [Filter.NextId]: lastID,
        [Filter.NextValue]: lastCreatedAt,
        [Filter.PrevId]: undefined,
        [Filter.PrevValue]: undefined,
      })
    );
  };

  const fetchPreviousPage = (): void => {
    setDirection("prev");
    dispatch(
      ReportsViewActions.updateFilter({
        [Filter.PageSize]: pageSize + 1,
        [Filter.PrevId]: firstID,
        [Filter.PrevValue]: firstCreatedAt,
        [Filter.NextId]: undefined,
        [Filter.NextValue]: undefined,
      })
    );
  };

  return {
    data: dataToDisplay,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage: hasMoreNext,
    hasPreviousPage: direction === "" ? false : hasMorePrev,
    pageSize,
    isFetching,
  };
};
